import apiClient from '../../lib/api-client';
import type { ApiResponse, PaginatedData, User, Department, Role, Permission, MenuVO, SystemConfig } from '../../types/models';

// Users
export async function listUsers(params?: Record<string, unknown>): Promise<PaginatedData<User>> {
  const { data } = await apiClient.get<ApiResponse<PaginatedData<User>>>('/users', { params });
  return data.data;
}

export async function listActiveUsers(): Promise<User[]> {
  const { data } = await apiClient.get<ApiResponse<User[]>>('/users/active');
  return data.data;
}

export async function updateUser(id: number, payload: Partial<User>): Promise<User> {
  const { data } = await apiClient.put<ApiResponse<User>>(`/users/${id}`, payload);
  return data.data;
}

export async function assignUserRoles(userId: number, roleIds: number[]): Promise<void> {
  await apiClient.put(`/users/${userId}/roles`, roleIds);
}

// Departments
export async function listDepartments(): Promise<Department[]> {
  const { data } = await apiClient.get<ApiResponse<Department[]>>('/departments');
  return data.data;
}

// Roles
export async function listRoles(params?: Record<string, unknown>): Promise<PaginatedData<Role>> {
  const { data } = await apiClient.get<ApiResponse<PaginatedData<Role>>>('/roles', { params });
  return data.data;
}

export async function createRole(payload: Partial<Role>): Promise<Role> {
  const { data } = await apiClient.post<ApiResponse<Role>>('/roles', payload);
  return data.data;
}

export async function updateRole(id: number, payload: Partial<Role>): Promise<Role> {
  const { data } = await apiClient.put<ApiResponse<Role>>(`/roles/${id}`, payload);
  return data.data;
}

export async function deleteRole(id: number): Promise<void> {
  await apiClient.delete(`/roles/${id}`);
}

// Permissions
export async function listPermissions(): Promise<Permission[]> {
  const { data } = await apiClient.get<ApiResponse<Permission[]>>('/permissions');
  return data.data;
}

export async function getRoleMenus(roleId: number): Promise<number[]> {
  const { data } = await apiClient.get<ApiResponse<number[]>>(`/roles/${roleId}/menus`);
  return data.data;
}

export async function assignRoleMenus(roleId: number, menuIds: number[]): Promise<void> {
  await apiClient.put(`/roles/${roleId}/menus`, { menu_ids: menuIds });
}

export async function getRoleUsers(roleId: number): Promise<User[]> {
  const { data } = await apiClient.get<ApiResponse<User[]>>(`/roles/${roleId}/users`);
  return data.data;
}

// System Configs

export interface WebsiteConfig {
  site_name?: string;
  site_logo?: string;
  site_description?: string;
  icp_number?: string;
  copyright_text?: string;
}

let _websiteConfigCache: WebsiteConfig | null = null;

export async function getPublicWebsiteConfig(): Promise<WebsiteConfig> {
  if (_websiteConfigCache) return _websiteConfigCache;
  try {
    const resp = await fetch('/api/v1/sys/config/public/website');
    const json = await resp.json();
    _websiteConfigCache = json.data || {};
  } catch {
    _websiteConfigCache = {};
  }
  return _websiteConfigCache ?? {};
}

export async function listConfigs(params?: Record<string, unknown>): Promise<PaginatedData<SystemConfig>> {
  const { data } = await apiClient.get<ApiResponse<PaginatedData<SystemConfig>>>('/sys/config', { params });
  return data.data;
}

export async function createConfig(payload: Partial<SystemConfig>): Promise<SystemConfig> {
  const { data } = await apiClient.post<ApiResponse<SystemConfig>>('/sys/config', payload);
  return data.data;
}

export async function updateConfig(id: number, payload: Partial<SystemConfig>): Promise<SystemConfig> {
  const { data } = await apiClient.put<ApiResponse<SystemConfig>>(`/sys/config/${id}`, payload);
  return data.data;
}
